import React, { useEffect, useState } from 'react';

const logo = process.env.PUBLIC_URL + '/logo.jpg';

const AppLoader = () => {
    const [progress, setProgress] = useState(0);
    const [stepIndex, setStepIndex] = useState(0);
    const [fadeStep, setFadeStep] = useState(true);
    const [isFadingOut, setIsFadingOut] = useState(false);

    const steps = [
        'Connecting to cloud...',
        'Loading your portfolio...',
        'Fetching market data...',
        'Preparing your dashboard...',
    ];

    // Progress counter
    useEffect(() => {
        const interval = setInterval(() => {
            setProgress(prev => {
                const next = prev + (100 / (4000 / 30));
                if (next >= 100) {
                    setIsFadingOut(true); // Trigger smooth CSS fade out
                    return 100;
                }
                return next;
            });
        }, 30);
        return () => clearInterval(interval);
    }, []);

    // Step cycling with fade transition
    useEffect(() => {
        const idx = Math.min(Math.floor((progress / 100) * steps.length), steps.length - 1);
        if (idx !== stepIndex) {
            setFadeStep(false);
            setTimeout(() => {
                setStepIndex(idx);
                setFadeStep(true);
            }, 200);
        }
    }, [progress]);

    const pct = Math.round(progress);

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            /* Black top-left → subtle purple bottom-right */
            background: 'linear-gradient(135deg, #000000 0%, #05010f 40%, #0d0020 70%, #160030 100%)',
            overflow: 'hidden',
            opacity: isFadingOut ? 0 : 1,
            transition: 'opacity 0.5s ease-in-out',
        }}>

            {/* ── Keyframes injected via <style> ── */}
            <style>{`
                @keyframes fa-spin {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(360deg); }
                }
                @keyframes fa-sparkle-rotate {
                    from { transform: rotate(0deg); }
                    to   { transform: rotate(-360deg); }
                }
                @keyframes fa-pulse-ring {
                    0%   { transform: scale(1);    opacity: 0.55; }
                    50%  { transform: scale(1.18); opacity: 0.12; }
                    100% { transform: scale(1);    opacity: 0.55; }
                }
                @keyframes fa-shimmer {
                    0%   { transform: translateX(-100%) skewX(-15deg); }
                    100% { transform: translateX(300%)  skewX(-15deg); }
                }
                @keyframes fa-glow-pulse {
                    0%, 100% { box-shadow: 0 0 30px 6px rgba(120,40,200,0.25); }
                    50%       { box-shadow: 0 0 55px 14px rgba(120,40,200,0.5); }
                }
                @keyframes fa-text-fade {
                    from { opacity: 0; transform: translateY(5px); }
                    to   { opacity: 1; transform: translateY(0); }
                }
                @keyframes fa-float {
                    0%, 100% { transform: translateY(0px);  }
                    50%       { transform: translateY(-7px); }
                }
                @keyframes fa-particle-drift {
                    0%   { transform: translate(0, 0)   scale(1);    opacity: 0; }
                    20%  { opacity: 1; }
                    100% { transform: translate(var(--dx), var(--dy)) scale(0.3); opacity: 0; }
                }
            `}</style>

            {/* ── Ambient corner glows ── */}
            <div style={{
                position: 'absolute', top: '-20%', left: '-15%',
                width: 480, height: 480, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(80,0,160,0.12) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute', bottom: '-15%', right: '-10%',
                width: 560, height: 560, borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(100,0,200,0.09) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            {/* ── Floating particle stars (static positions, CSS animated) ── */}
            {[
                { top: '15%', left: '12%', size: 2, delay: '0s', dx: '20px', dy: '-30px' },
                { top: '25%', left: '80%', size: 1.5, delay: '0.4s', dx: '-15px', dy: '-25px' },
                { top: '70%', left: '18%', size: 2, delay: '0.8s', dx: '10px', dy: '-40px' },
                { top: '65%', left: '75%', size: 1.5, delay: '1.2s', dx: '-20px', dy: '-20px' },
                { top: '40%', left: '5%', size: 1, delay: '0.6s', dx: '30px', dy: '-15px' },
                { top: '80%', left: '88%', size: 1, delay: '1s', dx: '-10px', dy: '-35px' },
                { top: '10%', left: '55%', size: 1.5, delay: '0.2s', dx: '5px', dy: '-20px' },
                { top: '90%', left: '40%', size: 1, delay: '1.4s', dx: '15px', dy: '-25px' },
            ].map((p, i) => (
                <div key={i} style={{
                    position: 'absolute', top: p.top, left: p.left,
                    width: p.size, height: p.size, borderRadius: '50%',
                    background: 'rgba(180,120,255,0.8)',
                    '--dx': p.dx, '--dy': p.dy,
                    animation: `fa-particle-drift 4s ease-in infinite ${p.delay}`,
                }} />
            ))}

            {/* ── Logo wrapper: pulse ring + spinning sparkle border + logo ── */}
            <div style={{ position: 'relative', width: 160, height: 160, marginBottom: 36 }}>

                {/* Pulse ring */}
                <div style={{
                    position: 'absolute', inset: -18,
                    borderRadius: '50%',
                    border: '1px solid rgba(140,60,255,0.35)',
                    animation: 'fa-pulse-ring 2.4s ease-in-out infinite',
                }} />
                <div style={{
                    position: 'absolute', inset: -30,
                    borderRadius: '50%',
                    border: '1px solid rgba(140,60,255,0.18)',
                    animation: 'fa-pulse-ring 2.4s ease-in-out infinite 0.7s',
                }} />

                {/* Spinning conic sparkle border ring */}
                <div style={{
                    position: 'absolute', inset: -6,
                    borderRadius: '50%',
                    padding: 3,
                    background: 'conic-gradient(from 0deg, transparent 0deg, rgba(160,80,255,0.9) 60deg, rgba(220,160,255,1) 90deg, rgba(160,80,255,0.9) 120deg, transparent 180deg, transparent 360deg)',
                    animation: 'fa-sparkle-rotate 3s linear infinite',
                    WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                    WebkitMaskComposite: 'xor',
                    maskComposite: 'exclude',
                }}>
                    <div style={{ width: '100%', height: '100%', borderRadius: '50%' }} />
                </div>

                {/* Glow pulse backing */}
                <div style={{
                    position: 'absolute', inset: 0, borderRadius: '50%',
                    animation: 'fa-glow-pulse 2.5s ease-in-out infinite',
                }} />

                {/* Spinning logo image */}
                <div style={{
                    width: '100%', height: '100%', borderRadius: '50%',
                    overflow: 'hidden',
                    border: '2px solid rgba(100,40,200,0.5)',
                    background: '#000',
                    animation: 'fa-spin 8s linear infinite, fa-float 3.5s ease-in-out infinite',
                    boxShadow: '0 0 32px 6px rgba(100,40,200,0.3), inset 0 0 20px rgba(0,0,0,0.6)',
                    position: 'relative', zIndex: 2,
                }}>
                    <img
                        src={logo}
                        alt="FinAssure Logo"
                        style={{
                            width: '100%', height: '100%',
                            objectFit: 'cover',
                            /* Counter-rotate so image stays upright while border spins */
                            /* Remove comment below if you WANT the image to spin too */
                            /* If spinning looks good keep as-is */
                        }}
                    />
                    {/* Specular gloss overlay */}
                    <div style={{
                        position: 'absolute', inset: 0,
                        background: 'radial-gradient(circle at 35% 25%, rgba(255,255,255,0.12) 0%, transparent 55%)',
                        borderRadius: '50%',
                    }} />
                </div>

                {/* Sparkle dots orbiting */}
                {[0, 72, 144, 216, 288].map((deg, i) => (
                    <div key={i} style={{
                        position: 'absolute',
                        width: 5, height: 5,
                        borderRadius: '50%',
                        background: i % 2 === 0 ? 'rgba(200,140,255,0.9)' : 'rgba(255,255,255,0.8)',
                        top: '50%', left: '50%',
                        boxShadow: '0 0 6px 2px rgba(180,100,255,0.7)',
                        transform: `rotate(${deg}deg) translateX(87px) translateY(-50%)`,
                        animation: `fa-sparkle-rotate ${3 + i * 0.3}s linear infinite`,
                        transformOrigin: '-67px 50%',
                    }} />
                ))}
            </div>

            {/* ── Brand name ── */}
            <div style={{
                animation: 'fa-text-fade 0.6s ease-out',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
                marginBottom: 28,
            }}>
                <span style={{
                    fontSize: 32,
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    fontFamily: "'Inter', 'Segoe UI', system-ui, sans-serif",
                    background: 'linear-gradient(90deg, #c084fc 0%, #ffffff 45%, #a855f7 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                }}>
                    FinAssure
                </span>
                <span style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: '0.38em',
                    color: 'rgba(180,130,255,0.55)',
                    fontFamily: "'Inter', monospace",
                    textTransform: 'uppercase',
                }}>
                    Financial Companion
                </span>
            </div>

            {/* ── Progress bar ── */}
            <div style={{ width: 260, position: 'relative', marginBottom: 14 }}>
                {/* Track */}
                <div style={{
                    width: '100%', height: 3, borderRadius: 99,
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.04)',
                    overflow: 'hidden',
                }}>
                    {/* Fill */}
                    <div style={{
                        height: '100%',
                        width: `${progress}%`,
                        background: 'linear-gradient(90deg, #7c3aed, #a855f7, #c084fc)',
                        borderRadius: 99,
                        transition: 'width 0.32s ease-out',
                        boxShadow: '0 0 14px 3px rgba(168,85,247,0.55)',
                        position: 'relative', overflow: 'hidden',
                    }}>
                        {/* Shimmer sweep */}
                        <div style={{
                            position: 'absolute', top: 0, left: 0,
                            width: '35%', height: '100%',
                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.45), transparent)',
                            animation: 'fa-shimmer 1.6s ease-in-out infinite',
                        }} />
                    </div>
                </div>

                {/* Percentage */}
                <div style={{
                    position: 'absolute', right: -36, top: '50%',
                    transform: 'translateY(-50%)',
                    fontSize: 9, fontWeight: 700,
                    color: 'rgba(180,130,255,0.6)',
                    fontFamily: 'monospace',
                    letterSpacing: '0.05em',
                }}>
                    {pct}%
                </div>
            </div>

            {/* ── Step label ── */}
            <span style={{
                fontSize: 10,
                fontWeight: 600,
                letterSpacing: '0.18em',
                color: 'rgba(200,160,255,0.45)',
                fontFamily: "'Inter', monospace",
                textTransform: 'uppercase',
                opacity: fadeStep ? 1 : 0,
                transform: fadeStep ? 'translateY(0)' : 'translateY(4px)',
                transition: 'opacity 0.2s ease, transform 0.2s ease',
            }}>
                {steps[stepIndex]}
            </span>
        </div>
    );
};

export default AppLoader;
